import type { Meta, StoryObj } from '@storybook/angular';
import { expect, within } from 'storybook/test';
import { ProductHighlightSectionComponent } from '../../../app/content/modules/page/components/product/components/product-highlight-section/product-highlight-section.component';

// Mock WineProduct with highlightText
const mockWineProductWithHighlight = {
  id: '1',
  title: 'Château Margaux 2015',
  nodePath: 'vin/chateau-margaux-2015',
  sku: 'CM2015',
  highlightText: '<p><strong>Særlig fremhævelse!</strong> Denne vin har modtaget en fantastisk anmeldelse fra Wine Spectator med 95 point. En sjælden mulighed for at opleve en af Bordeaux\' fineste vine.</p>',
} as any;

const meta: Meta<ProductHighlightSectionComponent> = {
  title: 'Organisms/Product/ProductHighlightSection',
  component: ProductHighlightSectionComponent,
  parameters: {
    docs: {
      description: {
        component: 'Displays a prominent highlight box for product pages to showcase special ratings, awards, or product news. Only renders when highlightText is present. Features a beige background with orange border matching Supervin brand colors.',
      },
    },
    layout: 'padded',
  },
  argTypes: {
    product: {
      description: 'The wine product with optional highlightText field',
      control: { type: 'object' },
    },
  },
  tags: ['autodocs'],
};

export default meta;
type Story = StoryObj<ProductHighlightSectionComponent>;

/**
 * Default highlight with mixed text formatting (bold headings and regular text)
 */
export const Default: Story = {
  args: {
    product: mockWineProductWithHighlight,
  },
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText(/særlig fremhævelse/i)).toBeInTheDocument();
  },
};

/**
 * Short announcement - brief 1-2 sentence highlight
 */
export const ShortText: Story = {
  args: {
    product: {
      ...mockWineProductWithHighlight,
      highlightText: '<p><strong>Nyt!</strong> Denne vin er nu tilgængelig i magnum format.</p>',
    } as any,
  },
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText(/nyt!/i)).toBeInTheDocument();
  },
};

/**
 * Long text to test read-more functionality with expanded content
 */
export const LongText: Story = {
  args: {
    product: {
      ...mockWineProductWithHighlight,
      highlightText: `
        <h3>Exceptionel årgang fra Château Margaux</h3>
        <p><strong>2015 årgangen</strong> af Château Margaux har modtaget enestående anerkendelse fra verdens førende vinkritikere:</p>
        <ul>
          <li><strong>Wine Spectator:</strong> 95 point - "En fantastisk Bordeaux med kompleks smag"</li>
          <li><strong>Robert Parker:</strong> 97 point - "Perfekt balance og elegance"</li>
          <li><strong>Jancis Robinson:</strong> 18/20 - "Vil udvikle sig smukt over de næste 10-15 år"</li>
        </ul>
        <p>Dette er en sjælden mulighed for at erhverve en af Bordeaux' absolut fineste vine fra en exceptionel årgang. Vinen er nu klar til at drikke, men vil fortsætte med at udvikle sig positivt i mange år fremover.</p>
        <p><em>Kun begrænset antal flasker tilgængelige.</em></p>
      `,
    } as any,
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-product-highlight-section')).toBeTruthy();
  },
};

/**
 * Highlight with embedded images (if TinyMCE allows image uploads)
 */
export const WithImages: Story = {
  args: {
    product: {
      ...mockWineProductWithHighlight,
      highlightText: `
        <h3>Eksklusiv præsentation</h3>
        <p><strong>Se vores nye anmeldelse video:</strong></p>
        <p>Denne vin har modtaget fantastiske anmeldelser fra internationale eksperter. Læs mere om hvad der gør denne årgang så speciel.</p>
      `,
    } as any,
  },
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText(/eksklusiv præsentation/i)).toBeInTheDocument();
  },
};

/**
 * Highlight with internal routing links
 */
export const WithLinks: Story = {
  args: {
    product: {
      ...mockWineProductWithHighlight,
      highlightText: `
        <p><strong>🏆 Vinder af Supervin Wine Awards 2024!</strong></p>
        <p>Denne vin blev kåret som årets bedste Bordeaux i vores årlige konkurrence. <a href="/vin/wine-awards-2024">Læs mere om vinderne her</a>.</p>
        <p>Oplev selv hvorfor eksperterne er begejstrede - bestil din flaske i dag!</p>
      `,
    } as any,
  },
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText(/vinder af supervin/i)).toBeInTheDocument();
  },
};

/**
 * No highlight text - component should not render
 */
export const NoHighlight: Story = {
  args: {
    product: {
      id: '1',
      title: 'Standard Product',
      sku: 'PROD001',
      // No highlightText field
    } as any,
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('.highlight-section')).toBeFalsy();
  },
};

/**
 * Empty highlight text - component should not render
 */
export const EmptyHighlight: Story = {
  args: {
    product: {
      ...mockWineProductWithHighlight,
      highlightText: '',
    } as any,
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('.highlight-section')).toBeFalsy();
  },
};

/**
 * Null highlight text - component should not render
 */
export const NullHighlight: Story = {
  args: {
    product: {
      ...mockWineProductWithHighlight,
      highlightText: null,
    } as any,
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('.highlight-section')).toBeFalsy();
  },
};
